/**
 * Client-side translation runtime.
 *
 * The Vite plugin transforms JSX string literals into __resolve() calls.
 * This module provides the dictionary store and the resolve function.
 *
 * Usage (automatic — developers don't call this directly):
 *   <Button>Save</Button>
 *   → becomes at build time →
 *   <Button>{__resolve("ui::Save")}</Button>
 *
 * The resolve function looks up the key in the in-memory dictionary.
 * If not found, returns the key itself (so the UI never breaks).
 */

import { appConfig, storageKey } from '@/config/app';

// ─── Dictionary store ─────────────────────────────────────────────

let dictionary: Record<string, string> = {};
let currentVersion = 0;
let currentLocale = 'en';
let isLoaded = false;

/** Listeners notified when dictionary changes */
const listeners = new Set<() => void>();

/** Subscribe to dictionary changes */
export function onDictionaryChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notifyListeners() {
  for (const fn of listeners) {
    try { fn(); } catch {}
  }
}

// ─── Public API ────────────────────────────────────────────────────

/** Get the current locale */
export function getLocale(): string {
  return currentLocale;
}

/**
 * Set the locale in memory and reload the dictionary.
 *
 * Pass `{ persist: true }` only when the user has *explicitly* chosen a
 * personal language (e.g. from a "My Language" picker). Auto-set values
 * from bootstrap loaders or org switches must not persist — otherwise the
 * stored value masquerades as a user override and shadows the org's
 * `default_language` for every subsequent session.
 */
export async function setLocale(
  locale: string,
  opts?: { persist?: boolean },
): Promise<void> {
  currentLocale = normalizeLocale(locale);
  if (opts?.persist) {
    localStorage.setItem(storageKey('locale_override'), currentLocale);
  }
  await loadDictionary();
}

/**
 * Read the user's explicit personal-locale override, or null if none.
 * Loaders use this to decide whether to honour a user choice over the
 * active org's `default_language`.
 */
export function getLocaleOverride(): string | null {
  const v = localStorage.getItem(storageKey('locale_override'));
  return v ? normalizeLocale(v) : null;
}

/** Clear the user's personal-locale override (revert to org default). */
export function clearLocaleOverride(): void {
  localStorage.removeItem(storageKey('locale_override'));
}

/**
 * Normalize a locale identifier to the 2-letter language code used by the
 * translations table (e.g., 'de-DE' → 'de', 'en-US' → 'en').
 */
export function normalizeLocale(locale: string): string {
  return locale.split('-')[0].toLowerCase();
}

/**
 * Pick a locale when no org context is available (e.g. /admin).
 * Honours an explicit user override, then falls back to browser language,
 * then English. Org-aware resolution lives in the route loaders.
 */
export function detectLocale(): string {
  const override = getLocaleOverride();
  if (override) return override;

  const browserLang = navigator.language?.split('-')[0];
  if (browserLang) return normalizeLocale(browserLang);

  return 'en';
}

// One-shot cleanup of the legacy `saas_locale` key, which used to be
// written by every setLocale() call and was conflated with user overrides.
// Removing it ensures pre-fix sessions don't keep shadowing org defaults.
try {
  localStorage.removeItem(storageKey('locale'));
} catch {}

/** Load the dictionary from the API */
export async function loadDictionary(orgId?: string): Promise<void> {
  try {
    const locale = currentLocale;
    const headers: Record<string, string> = {};
    if (orgId) {
      headers['X-Organization-Id'] = orgId;
    }

    const res = await fetch(`/api/translations/${locale}`, { headers });
    if (!res.ok) return;

    const json = await res.json();
    if (json.success && json.data) {
      dictionary = json.data.dictionary || {};
      currentVersion = json.data.version || 0;
      isLoaded = true;
      notifyListeners();
    }
  } catch {
    // Silent fail — dictionary stays as-is, keys display as fallback
  }
}

/** Check if dictionary version has changed (cheap check) */
export async function checkVersion(orgId?: string): Promise<boolean> {
  try {
    const headers: Record<string, string> = {};
    if (orgId) {
      headers['X-Organization-Id'] = orgId;
    }

    const res = await fetch(`/api/translations/${currentLocale}/version`, { headers });
    if (!res.ok) return false;

    const json = await res.json();
    const serverVersion = json.data?.version || 0;

    if (serverVersion !== currentVersion) {
      await loadDictionary(orgId);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/** Whether the dictionary has been loaded at least once */
export function isDictionaryLoaded(): boolean {
  return isLoaded;
}

// ─── Resolve function ──────────────────────────────────────────────

/**
 * Resolve a translation key to its display value.
 *
 * Called automatically by the Vite plugin's transformed code.
 * Developers should NOT call this directly — write plain strings in JSX.
 *
 * @param key - The dictionary key (format: "namespace::key")
 * @param vars - Optional interpolation variables: { count: 5 } replaces {{count}}
 * @returns The translated string, or the key itself as fallback
 */
export function __resolve(key: string, vars?: Record<string, string | number>): string {
  // Look up in dictionary
  let value = dictionary[key];

  // If not found, try stripping namespace prefix and returning raw key
  if (value === undefined) {
    const idx = key.indexOf('::');
    value = idx >= 0 ? key.substring(idx + 2) : key;
  }

  // Interpolate variables: {{varName}} → actual value
  if (vars) {
    for (const [varName, varValue] of Object.entries(vars)) {
      value = value.replace(new RegExp(`\\{\\{${varName}\\}\\}`, 'g'), String(varValue));
    }
  }

  return value;
}

/**
 * Manual translation helper for non-JSX contexts.
 *
 * Use this for strings in JS logic that the Vite auto-translate plugin
 * cannot reach: toast notifications, window.confirm() messages, ternary
 * expressions, array literals, etc.
 *
 * @param key - Plain English string (automatically prefixed with "ui::")
 * @returns The translated string, or the original key as fallback
 */
export function t(key: string): string {
  return __resolve(`ui::${key}`);
}

/**
 * Resolve a ROROS-namespace translation key.
 *
 * Used for seeded ROROS content whose title/description is stored as a
 * translation key (e.g. "roros.metrics.ftt_baseline.name") rather than a
 * literal string. The key is prefixed with the "roros::" namespace so the
 * dictionary lookup matches the rows inserted by `npm run seed:translations`
 * (and by the idempotent INSERT inside migration 290).
 *
 * Only call this when the value is known to be a translation key
 * (heuristic: value starts with "roros."). For user-authored content
 * (regular metric titles), use the value as-is.
 *
 * @param key - ROROS translation key without namespace prefix (e.g. "roros.metrics.ftt_baseline.name")
 * @returns The translated string, or the key itself as fallback
 */
export function tRoros(key: string): string {
  return __resolve(`roros::${key}`);
}

// Make __resolve globally available (the Vite plugin references it)
if (typeof window !== 'undefined') {
  (window as any).__resolve = __resolve;
}
